// Independent cycle view (DESIGN §2 screen 4 — Q24, optional entry).
//
// Deliberately NOT the default screen. Q24 settled on keeping a dedicated
// cycle surface as an *optional* entry point: the month grid is where the
// couple actually lives, and a cycle tracker promoted to the front page would
// be a tool for one feature rather than a calendar for two people.
//
// **W58.5 —— 這一頁從「四段」變成「三段」。**
//
// | 段 | W58.0–W58.4 | W58.5 |
// |---|---|---|
// | 倒數卡 | ✓ | ✓（不變） |
// | 推斷摘要 | ✓ | ✓（**拿掉「不一定準」那一行**） |
// | 近期關鍵日期 | ✓ | ✗ **併進時間軸** |
// | 這一輪的階段 | ✓ | ✗ **併進時間軸** |
// | **本週期階段時間軸** | ✗ | ✓（**新**） |
// | **歷史記錄（可就地編輯 / 刪除）** | 只有唯讀清單 | ✓（**新**） |
// | **每日紀錄（近 90 天）** | ✗ | ✓（**新**） |
//
// **「推算只是參考」只留一句。** 以前「不一定準」在這一頁出現**兩次**
// （推斷摘要一次、階段清單一次），日詳情還有一次。三份同一個免責聲明，
// 等於沒有特別提醒，只會讓人眼花 —— 而 DATA_MODEL §5 的重量要求它**存在**，
// 不是要求它**重複**。
//
// The history list exposes who recorded each entry. That is deliberate (Q19
// audit transparency) and not a leak — both partners can already see
// everything the other did, and hiding the recorder would make "TA 補正的
// 那筆" impossible to trace.

import type { CalendarCycle } from "@/lib/api/calendar";
import { CycleLateNotice } from "@/components/calendar/CycleLateNotice";
import { CycleSettingsPanel } from "@/components/calendar/CycleSettingsPanel";
import { CyclePhaseTimeline } from "@/components/calendar/CyclePhaseTimeline";
import { CycleHistoryList } from "@/components/calendar/CycleHistoryList";
import { CycleDailyLogs } from "@/components/calendar/CycleDailyLogs";
import { serverToday } from "@/components/calendar/cyclePeriod";
import { BreadcrumbParent } from "@/components/primitives/Breadcrumbs";

export interface CycleViewProps {
  cycle: CalendarCycle | null;
  loading: boolean;
  /** Q19: either partner may record a cycle. */
  onRecord: () => void;
  /**
   * Render the component's own header. `false` when the host already provides
   * a PageHero for the same title — the route wrapper does, and rendering both
   * put「協作記錄 / 週期」on screen twice.
   */
  showHeader?: boolean;
}

function fmt(d: string | null | undefined): string {
  if (!d) return "—";
  return `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;
}

/** Whole days between two ISO dates, date-only, no timezone involved. */
function daysBetween(a: string, b: string): number {
  return Math.round(
    (Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000,
  );
}

export function CycleView({
  cycle,
  loading,
  onRecord,
  showHeader = true,
}: CycleViewProps) {
  const projection = cycle?.projection ?? null;

  return (
    <div className="flex flex-col gap-s-4" data-testid="calendar-cycle-view">
      {showHeader ? (
        <header className="flex items-start justify-between gap-s-3">
          <div>
            <p className="text-micro uppercase tracking-wide text-ink-3">協作記錄</p>
            <h1 className="mt-s-1 text-h2 text-ink-1">週期</h1>
          </div>
          {/* W52.0 D-9 — `回到月曆` was the last plain-text back control in the
              app, and the reason the W45.0 audit reported this route as having
              no way back: it lives in `components/`, one level below the
              `routes/*.tsx` the scan read. It was alive the whole time.
              Now it is a breadcrumb trail, and the host page (`CalendarCycle`)
              owns the title. */}
          <BreadcrumbParent parentTo="/calendar" parentLabel="日曆" current="週期" parentTestId="calendar-cycle-back" />
        </header>
      ) : (
        // No header, so the host owns the title — the way back still has to
        // exist somewhere, and a route needs it (Q24's optional entry must
        // not be a dead end). The trail goes above rather than below, which is
        // where every other page puts it.
        <BreadcrumbParent parentTo="/calendar" parentLabel="日曆" current="週期" parentTestId="calendar-cycle-back" />
      )}

      {loading ? (
        <p className="text-body-sm text-ink-3">載入中…</p>
      ) : cycle && cycle.predicted_start ? (
        <>
          {/* W58.1 — 遲了幾天的溫馨提示 + 一鍵補記。 */}
          <CycleLateNotice daysLate={cycle.days_late} onRecord={onRecord} />
          {/* 倒數 / 階段時間軸都要 `projection`，推斷摘要不要 ——
              摘要的內容本來就只看 `predicted_start`。分開判斷是為了
              「有推算但投影是 null」時不會掉進「記錄滿 3 次」那一句：
              那句話在這種情況下是錯的。 */}
          {projection ? <CountdownCard cycle={cycle} /> : null}
          <InferenceSummary cycle={cycle} />
          {projection ? (
            <CyclePhaseTimeline
              projection={projection}
              predictedStart={cycle.predicted_start}
              today={serverToday(cycle)}
            />
          ) : null}
          {/* W58.5 — **審計補上的兩塊。** 見 `CycleDailyLogs` 與
              `CycleHistoryList` 的檔頭。 */}
          <CycleDailyLogs today={clientToday()} />
          <CycleHistoryList history={cycle.history} />
        </>
      ) : (
        <section
          className="rounded-md border border-hairline bg-paper-deep p-s-3"
          data-testid="calendar-cycle-insufficient"
        >
          <h2 className="text-micro uppercase tracking-wide text-ink-3">推斷摘要</h2>
          <p className="mt-s-2 text-body-sm text-ink-2">
            記錄滿 3 次才會顯示推算。
          </p>
        </section>
      )}

      {/* W58.1 — 設定（預測開關 / 經期長度 / 週期長度）。 */}
      {cycle?.settings ? <CycleSettingsPanel settings={cycle.settings} /> : null}

      <button
        type="button"
        className="tap-target self-start rounded-pill border border-hairline-strong px-s-4 py-s-2 text-body-sm text-ink-1 hover:bg-black/5"
        onClick={onRecord}
        data-testid="calendar-cycle-record"
      >
        ＋ 補記一次
      </button>

      <p className="text-meta text-ink-2">
        {/* W58.5 — 這句話現在**是真的**：週期是 `PARTNER_EDITABLE_CATEGORY`，
            也就是**雙方都能改、都能刪**任何一筆週期紀錄（不只是自己記的那筆）。 */}
        雙方都可以記錄，也可以修改或刪除任何一筆。
      </p>
    </div>
  );
}

/**
 * The countdown card — Meetyou's home screen, in one card.
 *
 * **The big number uses `gold`, which DESIGN §2 reserves for display numbers
 * and nothing else.** It is the one place in this screen where a number is
 * the *point* rather than a label.
 *
 * **The progress bar is the current phase's own progress**, not a percentage
 * of the cycle: 「經期第 3 天 / 共 5 天」 is the sentence a person actually
 * wants, and it is the one thing a bar can say that a number cannot.
 */
function CountdownCard({ cycle }: { cycle: CalendarCycle }) {
  const projection = cycle.projection;
  const days = projection?.days_until_next ?? null;
  const today = serverToday(cycle);

  let elapsed = 0;
  let total = 0;
  if (projection?.phase_start && projection?.phase_end && today) {
    total = daysBetween(projection.phase_start, projection.phase_end) + 1;
    elapsed = daysBetween(projection.phase_start, today) + 1;
    if (elapsed < 0) elapsed = 0;
    if (elapsed > total) elapsed = total;
  }
  const pct = total > 0 ? Math.round((elapsed / total) * 100) : 0;

  return (
    <section
      className="rounded-md border border-hairline bg-paper-deep p-s-4"
      data-testid="calendar-cycle-countdown"
    >
      <p className="text-micro uppercase tracking-wide text-ink-3">距離下次經期</p>
      <p className="mt-s-1 flex items-baseline gap-s-2">
        <span
          className="text-h1 font-semibold tabular-nums text-gold"
          data-testid="calendar-cycle-days-until"
        >
          {days ?? "—"}
        </span>
        <span className="text-body-sm text-ink-2">天</span>
      </p>
      <p className="mt-s-1 text-body-sm text-ink-2" data-testid="calendar-cycle-today-phase">
        {projection?.cycle_day ? `週期第 ${projection.cycle_day} 天` : ""}
        {projection?.cycle_day && projection?.phase_label ? " · " : ""}
        {projection?.phase_label ?? ""}
      </p>

      {total > 0 && projection?.phase_label ? (
        <div className="mt-s-3" data-testid="calendar-cycle-phase-progress">
          <div
            className="h-[6px] w-full overflow-hidden rounded-pill bg-hairline"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={elapsed}
            aria-label={`${projection.phase_label}進度，第 ${elapsed} 天，共 ${total} 天`}
          >
            <div
              className="h-full rounded-pill bg-partner/60"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-s-1 text-meta text-ink-3">
            {projection.phase_label}第 {elapsed} 天 · 共 {total} 天
          </p>
        </div>
      ) : null}
    </section>
  );
}

/** The inference summary — the same three numbers as before, plus two. */
function InferenceSummary({ cycle }: { cycle: CalendarCycle }) {
  const projection = cycle.projection;
  return (
    <section
      className="rounded-md border border-hairline bg-paper-deep p-s-3"
      data-testid="calendar-cycle-summary"
    >
      <h2 className="text-micro uppercase tracking-wide text-ink-3">推斷摘要</h2>
      <dl className="mt-s-3 grid grid-cols-3 gap-s-2">
        <div>
          <dt className="text-micro text-ink-3">平均週期</dt>
          <dd className="text-h3 tabular-nums text-ink-1">
            {cycle.average_interval_days} 天
          </dd>
        </div>
        <div>
          <dt className="text-micro text-ink-3">上次開始</dt>
          <dd className="text-h3 tabular-nums text-ink-1">{fmt(cycle.last_start)}</dd>
        </div>
        <div>
          <dt className="text-micro text-ink-3">預估下次</dt>
          <dd className="text-h3 tabular-nums text-you-strong">
            {fmt(cycle.predicted_start)}
          </dd>
        </div>
      </dl>
      {/* W58.0 — 平均經期長度是「自動計算時間」最直接的一項：建立經期事件
          時，前端就是拿這個數字自動套用結束日的。 */}
      {projection?.period_length_days ? (
        <dl className="mt-s-3 grid grid-cols-3 gap-s-2">
          <div>
            <dt className="text-micro text-ink-3">平均經期</dt>
            <dd
              className="text-h3 tabular-nums text-ink-1"
              data-testid="calendar-cycle-period-length"
            >
              {projection.period_length_days} 天
            </dd>
          </div>
          <div>
            <dt className="text-micro text-ink-3">信心</dt>
            {/* W58.2 — 用後端給的說明，不要在前端自己翻譯 confidence ——
                降級推算的兩種（preset / partial）有一個前端不知道的語意。
                W58.5：後端的措辭也改了（不再帶括號）。 */}
            <dd
              className="text-h3 text-ink-1"
              data-testid="calendar-cycle-confidence"
            >
              {cycle.confidence_label ?? confidenceLabel(cycle.confidence)}
            </dd>
          </div>
          <div>
            <dt className="text-micro text-ink-3">樣本</dt>
            <dd className="text-h3 tabular-nums text-ink-1">{cycle.sample_size} 筆</dd>
          </div>
        </dl>
      ) : null}
      {/*
        W58.5 — **這一頁唯一的免責聲明，而且它在這裡。**

        以前這一句是「根據 N 筆記錄估算，**不一定準**」，而同一頁的階段清單
        還有一句「排卵日按黃體期 14 天推算，**不一定準**」，日詳情又有一句
        —— **三份同一個免責聲明**。三份不是三次提醒，是一次提醒被稀釋成背景
        噪音。

        DATA_MODEL §5 的重量要求它**存在**，不要求它**重複**。所以現在
        **這一頁只有這一句**，而它在**推斷摘要**裡 —— 因為摘要只要有預測就
        一定在（時間軸只在有投影時才在，而有預測時投影可能不存在）。

        而且措辭軟下來了：「不一定準」是**否定**，「推算只是參考，多記幾次
        會更準」是**接下來的話** —— 後者告訴使用者該做什麼，前者只告訴他
        別相信。
      */}
      <p className="mt-s-3 text-meta text-ink-2" data-testid="calendar-cycle-disclaimer">
        推算只是參考，
        <strong className="font-semibold text-ink-1">多記幾次會更準</strong>。
      </p>
    </section>
  );
}

function confidenceLabel(confidence: string | null): string {
  switch (confidence) {
    case "high":
      return "相當穩定";
    case "medium":
      return "大致穩定";
    case "low":
      return "把握低";
    default:
      return "—";
  }
}

/** 今天（客戶端的日曆日）—— 給「每日紀錄」的查詢窗口用。 */
function clientToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}
